import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { KeyRound, ArrowRight, CheckCircle, Clock, BookOpen, GraduationCap } from '@/components/DeskeyIcons';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const G = '#34A853';

export default function LicenceActivationPage() {
  const nav = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const u = localStorage.getItem('desky_user');
    const t = localStorage.getItem('desky_token');
    if (!u || !t) { nav('/mydeskey'); return; }
    setUser(JSON.parse(u));
  }, [nav]);

  const handleActivate = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      const token = localStorage.getItem('desky_token');
      const res = await fetch(`${API_URL}/api/auth/licence/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ institution_code: code.trim().toUpperCase() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Activation failed');
      setSuccess(data.message);
      // Update local user
      const u = JSON.parse(localStorage.getItem('desky_user'));
      u.licence_status = 'pending';
      localStorage.setItem('desky_user', JSON.stringify(u));
      setUser(u);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  if (!user) return null;
  const isPending = user.licence_status === 'pending';
  const isActive = user.licence_status === 'active';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="licence-activation-page">
      <header className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button onClick={() => nav('/')} className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <span style={{ color: G }}>Deskey</span>
          </button>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">{user.email}</span>
            <button onClick={() => { localStorage.clear(); nav('/mydeskey'); }} className="text-xs text-red-500 hover:underline" data-testid="logout-btn">Logout</button>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          {/* Status indicator */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: `${G}10` }}>
              {isPending ? <Clock className="w-8 h-8 text-amber-500" /> :
               isActive ? <CheckCircle className="w-8 h-8" style={{ color: G }} /> :
               <KeyRound className="w-8 h-8" style={{ color: G }} />}
            </div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {isPending ? 'Approval Pending' : isActive ? 'Licence Active!' : 'Activate Your Licence'}
            </h1>
            <p className="text-sm text-gray-400 mt-2 max-w-sm mx-auto">
              {isPending ? 'Your institution admin has been notified. You will get full access once approved.' :
               isActive ? 'Your account is connected to your institution. Enjoy full access!' :
               'Enter the institution code provided by your school or university to get full access.'}
            </p>
          </div>

          {/* Active state */}
          {isActive && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center">
              <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: G }} />
              <p className="text-sm text-gray-600 mb-4">Your licence is active. You have full access to your institution's resources.</p>
              <Button onClick={() => nav(user.role === 'teacher' ? '/teacher' : '/student')} className="h-11 px-8 rounded-xl text-white font-semibold text-sm" style={{ background: G }} data-testid="go-dashboard-btn">
                Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Pending state */}
          {isPending && (
            <div className="bg-white rounded-2xl border border-amber-100 p-6 text-center">
              <div className="bg-amber-50 rounded-xl p-4 mb-4">
                <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-sm text-amber-700 font-medium">Waiting for institution approval</p>
                <p className="text-xs text-amber-600 mt-1">This usually takes less than 24 hours</p>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Meanwhile, you can explore the public knowledge base.
              </p>
              <Button onClick={() => nav('/')} variant="outline" className="mt-4 h-10 rounded-xl text-sm" data-testid="explore-btn">
                Explore Knowledge Base
              </Button>
            </div>
          )}

          {/* Activation form */}
          {!isPending && !isActive && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <form onSubmit={handleActivate} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Institution Code</label>
                  <Input data-testid="institution-code-input" placeholder="e.g. ABC123" value={code}
                    onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                    required maxLength={8}
                    className="h-12 rounded-xl border-gray-200 text-center text-lg tracking-[0.3em] font-mono uppercase" />
                </div>
                {error && <p className="text-xs text-red-500" data-testid="activation-error">{error}</p>}
                {success && <p className="text-xs text-green-600" data-testid="activation-success">{success}</p>}
                <Button type="submit" data-testid="activate-submit" disabled={loading || code.length < 4} className="w-full h-11 rounded-xl font-semibold text-white text-sm" style={{ background: G }}>
                  {loading ? 'Activating...' : 'Activate Licence'} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </form>

              <div className="mt-6 pt-4 border-t border-gray-100">
                <p className="text-[10px] text-gray-400 text-center">
                  Don't have an institution code? Ask your school admin or{' '}
                  <button onClick={() => nav('/')} className="underline" style={{ color: G }}>contact us</button>.
                </p>
              </div>
            </div>
          )}

          {/* What you get */}
          {!isActive && (
            <div className="mt-8 grid grid-cols-2 gap-3">
              {[
                { icon: BookOpen, text: 'AI-generated lessons' },
                { icon: GraduationCap, text: 'Personalized learning' },
              ].map((f, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
                  <f.icon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <span className="text-xs text-gray-500">{f.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
